import { useState, useEffect, useCallback, useMemo } from 'react';
import { wikiApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  BookOpen, Search, Plus, Pencil, Trash2, Save,
  ChevronDown, ChevronRight, FileText,
} from 'lucide-react';

const CATEGORIAS_ORDEN = [
  'General', 'Compras', 'Bodega', 'RRHH',
  'Contabilidad', 'Flujo de Caja', 'Configuración',
];

const emptyArticulo = (categoria = 'General') => ({
  titulo: '', categoria, contenido: '',
  orden: 0, tags: [], publicado: true,
});

// Configurar marked para que sea seguro y consistente
marked.setOptions({
  gfm: true,
  breaks: true,
  headerIds: false,
  mangle: false,
});

export default function ManualUso() {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [articulo, setArticulo] = useState(null);
  const [loadingArticulo, setLoadingArticulo] = useState(false);
  const [expandedCats, setExpandedCats] = useState(() => new Set(CATEGORIAS_ORDEN));

  // Editor
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyArticulo());
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (!isAdmin) params.publicado = true;
      const res = await wikiApi.list(params);
      setItems(res.data.items || []);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al cargar manual', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, isAdmin]);

  useEffect(() => { loadList(); }, [loadList]);

  const loadArticulo = useCallback(async (slug) => {
    if (!slug) {
      setArticulo(null);
      return;
    }
    setLoadingArticulo(true);
    try {
      const res = await wikiApi.get(slug);
      setArticulo(res.data);
    } catch {
      setArticulo(null);
    } finally {
      setLoadingArticulo(false);
    }
  }, []);

  useEffect(() => { loadArticulo(selectedSlug); }, [selectedSlug, loadArticulo]);

  const grouped = useMemo(() => {
    const g = {};
    for (const cat of CATEGORIAS_ORDEN) g[cat] = [];
    for (const it of items) {
      const cat = it.categoria || 'General';
      if (!g[cat]) g[cat] = [];
      g[cat].push(it);
    }
    // sort each
    for (const cat of Object.keys(g)) {
      g[cat].sort((a, b) => (a.orden || 0) - (b.orden || 0) || a.titulo.localeCompare(b.titulo));
    }
    return g;
  }, [items]);

  const toggleCategoria = (cat) => {
    setExpandedCats((prev) => {
      const ns = new Set(prev);
      if (ns.has(cat)) ns.delete(cat);
      else ns.add(cat);
      return ns;
    });
  };

  const openNew = (categoriaPrefer = 'General') => {
    setForm(emptyArticulo(categoriaPrefer));
    setEditing('new');
  };

  const openEdit = (articulo) => {
    setForm({
      titulo: articulo.titulo,
      categoria: articulo.categoria,
      contenido: articulo.contenido || '',
      orden: articulo.orden || 0,
      tags: articulo.tags || [],
      publicado: articulo.publicado !== false,
    });
    setEditing(articulo.id);
  };

  const save = async () => {
    if (!form.titulo.trim()) {
      toast({ title: 'Título requerido', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      let updated;
      if (editing === 'new') {
        const res = await wikiApi.create(form);
        updated = res.data;
        toast({ title: 'Artículo creado' });
      } else {
        const res = await wikiApi.update(editing, form);
        updated = res.data;
        toast({ title: 'Artículo actualizado' });
      }
      setEditing(null);
      await loadList();
      if (updated?.slug) setSelectedSlug(updated.slug);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (art) => {
    if (!window.confirm(`¿Eliminar el artículo "${art.titulo}"?`)) return;
    try {
      await wikiApi.remove(art.id);
      toast({ title: 'Artículo eliminado' });
      if (selectedSlug === art.slug) setSelectedSlug(null);
      await loadList();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al eliminar', variant: 'destructive' });
    }
  };

  const html = useMemo(() => {
    if (!articulo?.contenido) return '';
    try {
      const raw = marked.parse(articulo.contenido);
      // Sanitizar HTML para prevenir XSS (script tags, event handlers, etc.)
      return DOMPurify.sanitize(raw, {
        ALLOWED_TAGS: [
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'p', 'br', 'hr', 'div', 'span',
          'strong', 'em', 'b', 'i', 'u', 's', 'mark',
          'ul', 'ol', 'li',
          'blockquote', 'code', 'pre',
          'a', 'img',
          'table', 'thead', 'tbody', 'tr', 'th', 'td',
        ],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class'],
        ALLOW_DATA_ATTR: false,
      });
    } catch {
      return DOMPurify.sanitize(`<pre>${articulo.contenido}</pre>`);
    }
  }, [articulo]);

  return (
    <div className="p-4 space-y-3" data-testid="manual-uso-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6" />
            Manual de Uso
          </h1>
          <p className="text-sm text-muted-foreground">
            Documentación interna del sistema · {items.length} artículo{items.length !== 1 ? 's' : ''}
          </p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => openNew()} data-testid="btn-nuevo-articulo">
            <Plus className="w-4 h-4 mr-1" /> Nuevo artículo
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-3">
        {/* Sidebar artículos */}
        <Card>
          <CardContent className="p-3 space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                data-testid="input-buscar-wiki"
              />
            </div>

            {loading ? (
              <div className="space-y-1.5">
                <Skeleton className="h-6" />
                <Skeleton className="h-6" />
                <Skeleton className="h-6" />
              </div>
            ) : items.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                {search
                  ? 'Sin resultados'
                  : 'No hay artículos. ' + (isAdmin ? 'Crea el primero.' : 'Pídele a un admin que cree el manual.')}
              </p>
            ) : (
              <div className="space-y-0.5 max-h-[70vh] overflow-y-auto">
                {CATEGORIAS_ORDEN.map((cat) => {
                  const arts = grouped[cat] || [];
                  if (arts.length === 0) return null;
                  const expanded = expandedCats.has(cat);
                  return (
                    <div key={cat}>
                      <button
                        onClick={() => toggleCategoria(cat)}
                        className="w-full flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                        data-testid={`cat-${cat.replace(/\s+/g, '-')}`}
                      >
                        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        <span className="flex-1 text-left">{cat}</span>
                        <Badge variant="outline" className="text-[10px] h-4 px-1.5">{arts.length}</Badge>
                      </button>
                      {expanded && (
                        <div className="ml-4 space-y-0.5">
                          {arts.map((a) => {
                            const isSel = selectedSlug === a.slug;
                            return (
                              <button
                                key={a.id}
                                onClick={() => setSelectedSlug(a.slug)}
                                className={`w-full flex items-start gap-1.5 px-2 py-1 text-[12px] rounded text-left hover:bg-blue-50 dark:hover:bg-blue-950/30 ${isSel ? 'bg-blue-100 dark:bg-blue-900/40 font-medium' : ''}`}
                                data-testid={`art-${a.slug}`}
                              >
                                <FileText className="w-3 h-3 mt-0.5 flex-shrink-0 text-slate-400" />
                                <span className="flex-1 truncate">{a.titulo}</span>
                                {!a.publicado && (
                                  <Badge variant="outline" className="text-[9px] h-4 px-1">draft</Badge>
                                )}
                              </button>
                            );
                          })}
                          {isAdmin && (
                            <button
                              onClick={() => openNew(cat)}
                              className="w-full flex items-center gap-1 px-2 py-1 text-[11px] text-muted-foreground hover:text-blue-600"
                              data-testid={`add-art-${cat.replace(/\s+/g, '-')}`}
                            >
                              <Plus className="w-3 h-3" /> Agregar
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Visor */}
        <Card>
          <CardContent className="p-5">
            {loadingArticulo ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-1/2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : !articulo ? (
              <div className="text-center py-16 text-muted-foreground">
                <BookOpen className="w-12 h-12 mx-auto opacity-30 mb-3" />
                <p className="text-sm">Selecciona un artículo de la izquierda para verlo.</p>
                {items.length === 0 && isAdmin && (
                  <p className="text-xs mt-2">No hay artículos aún. Crea el primero con el botón "Nuevo artículo".</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <Badge variant="outline" className="text-[10px] mb-1">{articulo.categoria}</Badge>
                    <h2 className="text-2xl font-bold">{articulo.titulo}</h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Última actualización: {articulo.updated_at?.split('T')[0]}
                      {articulo.updated_by ? ` · ${articulo.updated_by}` : ''}
                      {!articulo.publicado && <Badge variant="outline" className="ml-2 text-[10px]">Borrador</Badge>}
                    </p>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(articulo)} data-testid="btn-edit-articulo">
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(articulo)} data-testid="btn-delete-articulo">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                {articulo.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {articulo.tags.map((t) => (
                      <Badge key={t} variant="outline" className="text-[10px]">#{t}</Badge>
                    ))}
                  </div>
                )}
                {!articulo.contenido?.trim() ? (
                  <p className="text-sm text-muted-foreground italic py-6 text-center">
                    Este artículo aún no tiene contenido.
                  </p>
                ) : (
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none wiki-content"
                    // SECURITY: `html` is pre-sanitized via DOMPurify with an explicit
                    // tag/attr allowlist (see useMemo at line 172). Markdown rendered
                    // by `marked` is NEVER injected raw — only the DOMPurify output.
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="dialog-editor">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nuevo artículo' : 'Editar artículo'}</DialogTitle>
            <DialogDescription>
              Soporta <strong>Markdown</strong>: <code>#</code> headers, <code>**bold**</code>, <code>- listas</code>, <code>[link](url)</code>, <code>```código```</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 flex-1 overflow-y-auto">
            <div className="col-span-2">
              <Label>Título *</Label>
              <Input
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                data-testid="input-titulo"
              />
            </div>
            <div>
              <Label>Categoría</Label>
              <select
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                className="w-full h-9 text-sm border rounded px-2 bg-white dark:bg-slate-900"
                data-testid="select-categoria"
              >
                {CATEGORIAS_ORDEN.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>Orden</Label>
              <Input
                type="number"
                value={form.orden}
                onChange={(e) => setForm({ ...form, orden: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="col-span-2">
              <Label>Tags (separados por coma)</Label>
              <Input
                value={(form.tags || []).join(', ')}
                onChange={(e) => setForm({ ...form, tags: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                placeholder="oc, aprobacion, gerencia..."
              />
            </div>
            <div className="col-span-2">
              <Label>Contenido (Markdown)</Label>
              <textarea
                value={form.contenido}
                onChange={(e) => setForm({ ...form, contenido: e.target.value })}
                rows={14}
                className="w-full text-sm border rounded p-2 font-mono bg-white dark:bg-slate-900 resize-y"
                placeholder={`# Título principal\n\nIntro al artículo.\n\n## Pasos\n\n1. Primer paso\n2. Segundo paso\n\n**Tip:** algo importante.`}
                data-testid="textarea-contenido"
              />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch
                checked={form.publicado}
                onCheckedChange={(v) => setForm({ ...form, publicado: v })}
                data-testid="switch-publicado"
              />
              <Label>Publicado (visible para todos los usuarios)</Label>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-articulo">
              <Save className="w-3.5 h-3.5 mr-1" />
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Estilos para markdown */}
      <style>{`
        .wiki-content h1 { font-size: 1.5rem; font-weight: 700; margin-top: 1.2em; margin-bottom: 0.5em; border-bottom: 1px solid var(--border); padding-bottom: 0.3em; }
        .wiki-content h2 { font-size: 1.25rem; font-weight: 700; margin-top: 1em; margin-bottom: 0.4em; }
        .wiki-content h3 { font-size: 1.1rem; font-weight: 600; margin-top: 0.8em; margin-bottom: 0.3em; }
        .wiki-content p { margin: 0.6em 0; line-height: 1.6; }
        .wiki-content ul, .wiki-content ol { margin: 0.6em 0; padding-left: 1.5em; }
        .wiki-content ul li { list-style: disc; margin: 0.2em 0; }
        .wiki-content ol li { list-style: decimal; margin: 0.2em 0; }
        .wiki-content code { background: rgba(120,120,120,0.15); padding: 0.1em 0.4em; border-radius: 4px; font-size: 0.85em; font-family: ui-monospace, monospace; }
        .wiki-content pre { background: rgba(0,0,0,0.05); padding: 0.8em; border-radius: 6px; overflow-x: auto; margin: 0.8em 0; }
        .wiki-content pre code { background: none; padding: 0; }
        .wiki-content blockquote { border-left: 3px solid #3b82f6; padding-left: 0.8em; margin: 0.8em 0; color: var(--muted-foreground); font-style: italic; }
        .wiki-content a { color: #2563eb; text-decoration: underline; }
        .wiki-content table { border-collapse: collapse; margin: 0.8em 0; }
        .wiki-content th, .wiki-content td { border: 1px solid var(--border); padding: 0.4em 0.7em; }
        .wiki-content th { background: rgba(120,120,120,0.1); font-weight: 600; }
        .wiki-content strong { font-weight: 700; }
      `}</style>
    </div>
  );
}
